import { useEffect, useState } from "react"
import { Text, Button, Avatar } from "../atoms"
import { CardListItem, Chip } from "../molecules"
import { useModal } from "../molecules/Modal/ModalProvider"
import { getAllMentors, unlinkSpecialistMentors } from "@/services/mentoringServices"
import { MentorsDataType } from "@/types/mentoring"
import { useAuth } from "@/contexts/AuthContext"
import { useToast } from "@/hooks/useToast"
import { getMentorPermissions } from "@/utils/mentorPermissions"
import { AddSpecialistMentorsModalContent } from "./AddSpecialistMentorsModalContent"

interface CompaniesFormTabMentorsProps {
  companyId: string
  companyMentorId?: number
}

export function CompaniesFormTabMentors({
  companyId,
  companyMentorId,
}: CompaniesFormTabMentorsProps) {
  const { user } = useAuth()
  const showToast = useToast()
  const { showModal, hideModal } = useModal()
  const [mentors, setMentors] = useState<MentorsDataType[]>([])

  const permissions = getMentorPermissions({ user, companyMentorId })

  const fetchMentors = async () => {
    getAllMentors(companyId)
      .then(({ data }) => setMentors(data))
      .catch((error) => console.error("Error loading mentors:", error))
  }

  useEffect(() => {
    fetchMentors()
  }, [companyId])

  const handleUnlinkMentor = async (mentorId: number) => {
    unlinkSpecialistMentors(user.emp_id, mentorId, parseInt(companyId))
      .then(() => {
        showToast({
          message: "Mentor desvinculado com sucesso!",
          variant: "success",
        })
        fetchMentors()
      })
      .catch((error) => {
        showToast({
          message: "Erro ao desvincular mentor!",
          variant: "error",
        })
        console.error("Error unlinking mentor:", error)
      })
  }

  const handleOpenAddMentorsModal = () => {
    showModal({
      title: "Adicionar mentores",
      size: "lg",
      content: (
        <AddSpecialistMentorsModalContent
          companyId={companyId}
          excludedMentorIds={mentors
            .map((mentor) => mentor.mentor_id ?? mentor.id)
            .filter((mentorId): mentorId is number => mentorId !== undefined)}
          onSuccess={() => {
            hideModal()
            fetchMentors()
          }}
        />
      ),
    })
  }

  return (
    <section
      data-state="open"
      className="flex flex-col gap-4 data-[state=open]:duration-700 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-bottom-5"
    >
      {permissions.canManage && (
        <div className="flex justify-end">
          <Button
            icon="user-add"
            onClick={handleOpenAddMentorsModal}
          >
            Adicionar mentores
          </Button>
        </div>
      )}

      <div className="flex flex-col gap-4">
        {mentors
          .sort((a, b) => (b.tipo === 1 ? 1 : -1))
          .map((item) => {
            const canUnlink = permissions.canUnlink(item)

            return (
              <CardListItem
                className={`relative overflow-hidden ${item.tipo === 1 ? "border border-primary" : ""}`}
                key={item.id}
                cols={3}
              >
                <div className="col-span-2 flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <Avatar
                      size="md"
                      alt={item.nome}
                    />
                    <Text variant="h6">{item.nome}</Text>
                  </div>
                </div>
                <div className="z-50 flex items-center justify-end gap-2">
                  {item.tipo === 1 ? (
                    <Chip variant="primary">
                      <i className="fi fi-ss-star mr-1 flex items-center justify-center text-[.9rem]"></i>
                      Principal
                    </Chip>
                  ) : (
                    <Chip variant="info">Especialista</Chip>
                  )}

                  {canUnlink && (
                    <Button
                      title="Desvincular Mentor"
                      data-position="left"
                      icon="broken-chain-link-wrong"
                      iconOnly
                      variant="ghost"
                      className="text-error hover:bg-error/10"
                      onClick={() => handleUnlinkMentor(item.id!)}
                    />
                  )}
                </div>
              </CardListItem>
            )
          })}
      </div>
    </section>
  )
}
